import { Tooltip } from '@clickguard/design-system'
import {
	CampaignDTO,
	CampaignStatusGroup,
	getCampaignStatusGroup
} from 'components/breadcrumb-bar/campaign-status'

/**
 * The campaign's status ON THE AD PLATFORM, not in ClickGuard (DEV-2422 §2.2).
 *
 * One filled disc at a single size for all three states. A small dot for one state and a
 * 16px outline icon for another are different widths, which moves the campaign name off
 * the account name above it — and the child row shares that column with its parent.
 *
 * The status itself comes from the ONE shared mapper in
 * `breadcrumb-bar/campaign-status.ts`; only the drawing lives here.
 */

const META: Record<CampaignStatusGroup, { word: string; fill: string }> = {
	ACTIVE: { word: 'enabled', fill: 'rgb(var(--cg-success))' },
	PAUSED: { word: 'paused', fill: 'rgb(var(--cg-icon-primary))' },
	REMOVED: { word: 'removed', fill: 'rgb(var(--cg-danger))' }
}

const PLATFORM_LABEL: Record<string, string> = {
	GADS: 'Google Ads',
	FADS: 'Meta Ads',
	BADS: 'Microsoft Ads'
}

/** The knocked-out glyph on the disc. `--cg-icon-inverse` is always white. */
const GLYPH = 'block rounded-full bg-[rgb(var(--cg-icon-inverse))]'

const SIZE = 16
/** 2px bars match the icon-pack stroke weight beside them. */
const BAR = Math.max(2, Math.round(SIZE * 0.125))
/** The X sits on the disc's diagonal, so its box is wider than the pause bars are tall. */
const MARK = Math.round(SIZE * 0.56)

const StatusDisc = ({ status }: { status: CampaignStatusGroup }) => (
	<span
		aria-hidden
		className="inline-flex shrink-0 items-center justify-center rounded-full"
		style={{ width: SIZE, height: SIZE, backgroundColor: META[status].fill }}
	>
		{status === 'PAUSED' && (
			<span className="flex items-center" style={{ gap: BAR }}>
				<span
					className={GLYPH}
					style={{ width: BAR, height: Math.round(SIZE * 0.44) }}
				/>
				<span
					className={GLYPH}
					style={{ width: BAR, height: Math.round(SIZE * 0.44) }}
				/>
			</span>
		)}
		{status === 'REMOVED' && (
			<span className="relative" style={{ width: MARK, height: MARK }}>
				<span
					className={`${GLYPH} absolute left-0 top-1/2 w-full`}
					style={{ height: BAR, transform: 'translateY(-50%) rotate(45deg)' }}
				/>
				<span
					className={`${GLYPH} absolute left-0 top-1/2 w-full`}
					style={{ height: BAR, transform: 'translateY(-50%) rotate(-45deg)' }}
				/>
			</span>
		)}
	</span>
)

export const CampaignStatusMark = ({
	campaign,
	platform
}: {
	campaign: CampaignDTO
	platform?: string
}) => {
	const status = getCampaignStatusGroup(campaign)
	// Bing `Suspended`, Meta `IN_PROCESS` / `WITH_ISSUES` and anything unknown fall into
	// no bucket. The slot around this still holds its width, so the name does not shift.
	if (!status) return null

	return (
		<Tooltip
			content={`Campaign is ${META[status].word} on ${
				PLATFORM_LABEL[platform ?? ''] ?? 'this ad platform'
			}`}
			position="top"
			// The table wrapper is `overflow-x-auto`, so a tooltip drawn in place is cut
			// off — it has to go to a portal, the same as the issue label above it.
			portal
			zIndex={60}
		>
			<span className="inline-flex shrink-0">
				<StatusDisc status={status} />
			</span>
		</Tooltip>
	)
}
